import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { EaRule, WcagRule } from '@eye-able/types';
import { queryKeys, storageUrl } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

type PartialCatalogue<T> = Record<string, Partial<T>>;

/** The Eye-Able rule texts of one language, as `useFetchRules` loads them. */
export function eaRulesQueryOptions(lng: string | undefined) {
  return {
    enabled: Boolean(lng),
    queryKey: [queryKeys.EYEABLE_RULES, lng],
    retryOnMount: false,
    queryFn: (): Promise<PartialCatalogue<EaRule>> =>
      fetchWrapper.get<PartialCatalogue<EaRule>>(
        `${storageUrl}audit-json-files/ea_rules_${lng}.json`,
      ),
  };
}

export interface RulesCatalogueResponse {
  isLoading: boolean;
  isError: boolean;
  eaRules: Record<string, EaRule> | undefined;
  wcagRules: Record<string, WcagRule> | undefined;
  language: string | undefined;
  rulesUpdatedAt: number;
}

export function useFetchRules(): RulesCatalogueResponse {
  const { i18n, ready } = useInlineTranslation();
  const lng = i18n.resolvedLanguage;

  const queries = useQueries({
    queries: [
      { ...eaRulesQueryOptions(lng), enabled: Boolean(ready && lng) },
      {
        enabled: Boolean(ready && lng),
        queryKey: [queryKeys.WCAG_RULES, lng],
        retryOnMount: false,
        queryFn: async (): Promise<PartialCatalogue<WcagRule>> => {
          return fetchWrapper.get<PartialCatalogue<WcagRule>>(
            `${storageUrl}audit-json-files/wcag_rules_${lng}.json`,
          );
        },
      },
      {
        queryKey: [queryKeys.EYEABLE_RULES_META],
        retryOnMount: false,
        queryFn: async (): Promise<PartialCatalogue<EaRule>> => {
          return fetchWrapper.get<PartialCatalogue<EaRule>>(
            `${storageUrl}audit-json-files/ea_rules_meta.json`,
          );
        },
      },
      {
        queryKey: [queryKeys.WCAG_RULES_META],
        retryOnMount: false,
        queryFn: async (): Promise<PartialCatalogue<WcagRule>> => {
          return fetchWrapper.get<PartialCatalogue<WcagRule>>(
            `${storageUrl}audit-json-files/wcag_rules_meta.json`,
          );
        },
      },
      {
        enabled: Boolean(ready && lng),
        queryKey: [queryKeys.AI_RULES, lng],
        retry: false,
        queryFn: async (): Promise<PartialCatalogue<EaRule>> => {
          try {
            return await fetchWrapper.get<PartialCatalogue<EaRule>>(
              `${storageUrl}ai-test-json-files/ai_rules_${lng}.json`,
            );
          } catch {
            return {};
          }
        },
      },
      {
        queryKey: [queryKeys.AI_RULES_META],
        retry: false,
        queryFn: async (): Promise<PartialCatalogue<EaRule>> => {
          try {
            return await fetchWrapper.get<PartialCatalogue<EaRule>>(
              `${storageUrl}ai-test-json-files/ai_rules_meta.json`,
            );
          } catch {
            return {};
          }
        },
      },
    ],
  });

  const [
    eaRulesLangQuery,
    wcagRulesLangQuery,
    eaRulesMetaQuery,
    wcagRulesMetaQuery,
    aiRulesLangQuery,
    aiRulesMetaQuery,
  ] = queries;
  const coreQueries = queries.slice(0, 4);

  const eaRules = useMemo(() => {
    const lang = eaRulesLangQuery.data;
    const meta = eaRulesMetaQuery.data;
    if (!lang || !meta) return undefined;

    const merged = Object.fromEntries(
      Object.entries(lang).map(([ruleId, rule]) => [
        ruleId,
        { ...rule, ...meta[ruleId] },
      ]),
    ) as Record<string, EaRule>;

    const aiLang = aiRulesLangQuery.data;
    const aiMeta = aiRulesMetaQuery.data;
    if (aiLang && aiMeta) {
      for (const [ruleId, rule] of Object.entries(aiLang)) {
        if (!aiMeta[ruleId]) continue;
        merged[ruleId] = { ...rule, ...aiMeta[ruleId] } as EaRule;
      }
    }

    return merged;
  }, [
    aiRulesLangQuery.data,
    aiRulesMetaQuery.data,
    eaRulesLangQuery.data,
    eaRulesMetaQuery.data,
  ]);

  const wcagRules = useMemo(() => {
    const lang = wcagRulesLangQuery.data;
    const meta = wcagRulesMetaQuery.data;
    if (!lang || !meta) return undefined;

    return Object.fromEntries(
      Object.entries(lang).map(([ruleId, rule]) => [
        ruleId,
        { ...rule, ...meta[ruleId] },
      ]),
    ) as Record<string, WcagRule>;
  }, [wcagRulesLangQuery.data, wcagRulesMetaQuery.data]);

  return {
    isLoading: queries.some((query) => query.isLoading),
    isError: coreQueries.some((query) => query.isError),
    eaRules,
    wcagRules,
    language: lng,
    rulesUpdatedAt: Math.max(...queries.map((query) => query.dataUpdatedAt)),
  };
}
